import { AfterViewInit, ChangeDetectorRef, Component, Input, NgZone, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { ChatFeedback, Message, WingsAssistantChatService, AgentRunRequest, ToolCall } from "../services/wings-assistant-chat.service";
import { ChatTranslations, ChatTranslationService } from "../services/chat-translation.service";
import { MsalService } from "@azure/msal-angular";
import { EventMessage, EventType } from "@azure/msal-browser";

interface Conversation {
  id: string | number; // Can be temporary (e.g., 'temp-1') or permanent (string from backend)
  messages?: Message[];
  title?: string; 
  isNew?: boolean; // To track if it's a temp conversation awaiting permanent ID
  timestamp: Date;
  isActive ?: boolean;
  isWaitingResponse ?: boolean;
  isExpired ?: boolean;
}

export interface AttachedFile {
  file: File;
  name: string;
  size: number;
  type: string;
  preview?: string;
  base64?: string;
}

@Component({
    selector: "wings-assistant-chat",
    standalone: false,
    templateUrl: "wings-assistant-chat.html",
    styleUrls: ["wings-assistant-chat.scss"],
})
export class WingsAssistantChatComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() public language: string = "pt-BR"; // Idioma padrão

    private cdsid = "";

    public isOpen = false;
    public isMinimized = false;
    public isFullscreen = false;
    public isResizing = false;
    public currentMessage = "";
    public isTyping = false;
    public chatWidth = 400;
    public chatHeight = 600;
    public sessionVerification : any | undefined;
    private savedHeight: number = 600;
    public translations: ChatTranslations; // Correctly typed
    
    // Propriedades para o sistema de feedback
    public showFeedbackModal = false;
    public currentFeedbackMessage: Message | null = null;
    public feedbackRating = 0;
    public feedbackComment = '';
    public isSendingFeedback = false;
    
    public conversations: Conversation[] = [];
    public activeConversationId: string | number | null = null;
    public activeConversation: Conversation | null = null;
    public loadingConversationIds: Set<string | number> = new Set(); // IDs das conversas sendo carregadas
    public feedbackMessageIndexes: Array<number> = []; // Índices das mensagens que já receberam feedback
    public demoMessage : Message = null;
    public hasEmptyConversations = true;
    public streamSubscription: Subscription | null = null; // Subscription do streaming SSE
    private typingInterval: any = null; // Intervalo para animação de digitação

    // Propriedades para gerenciamento de anexos
    public attachedFiles: AttachedFile[] = [];
    public maxFileSize: number = 10 * 1024 * 1024; // 10MB
    public allowedFileTypes: string[] = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'text/plain', 'text/csv',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // xlsx
        'application/vnd.ms-excel', // xls
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // docx
        'application/msword', // doc
        'application/vnd.ms-outlook', // msg
        'video/mp4' // mp4
    ];
    private allowedExtensions: string[] = ['.msg', '.mp4'];
    public maxFiles: number = 5;

    public sessionUserInfoString: string = "";

    public sessionUserInfo: any = null;
    public state_delta: any = {};

    // Sistema de novidades (onboarding)
    public showNewsBubble: boolean = false;
    public showNewsModal: boolean = false;
    public isFirstTimeUser: boolean = false;
    private readonly NEWS_SEEN_KEY = 'wings-chat-news-seen-v1';

    // Prompts de exemplo (serão carregados das traduções)
    public examplePrompts: Array<{text: string, icon: string}> = [];

    // Sistema de sugestões autocomplete
    public showSuggestions: boolean = false;
    public selectedSuggestionIndex: number = -1;
    private suggestionKeywords: Map<string, Array<{text: string, icon: string}>> = new Map();

    // Flag para evitar múltiplas inicializações
    private isInitialized: boolean = false;
    private isInitializing: boolean = false;

    // Getter para verificar se o usuário é do time WINGS - IT
    public get isWingsIT(): boolean {
        if (!this.sessionUserInfo) return false;
        const jobTitleList = this.sessionUserInfo.jobTitleList || [];
        return jobTitleList.some((title: string) => 
            title.toUpperCase().includes('WINGS') && title.toUpperCase().includes('IT')
        );
    }

    /**
     * Retorna o perfil do usuário (jobTitle) para enviar ao backend
     */
    private getUserProfile(): string | undefined {
        if (this.sessionUserInfo && this.sessionUserInfo.jobTitle) {
            return this.sessionUserInfo.jobTitle;
        }
        if (this.sessionUserInfo && this.sessionUserInfo.jobTitleList && this.sessionUserInfo.jobTitleList.length > 0) {
            return this.sessionUserInfo.jobTitleList[0];
        }
        return undefined;
    }
    private eventCallbackId: string | undefined;
    constructor(
        private chatAssistantService: WingsAssistantChatService,
        private translationService: ChatTranslationService,
        private ref: ChangeDetectorRef,
        private ngZone: NgZone,
        private msalService: MsalService
    ) {
        // Adicionar listener para redimensionamento da janela
        window.addEventListener('resize', () => {
            if (!this.isResizing) { // Só ajustar se não estiver redimensionando manualmente
                this.adjustToScreenSize();
            }
        });
    }

    ngAfterViewInit() {
    }

    async ngOnInit() {
        this.translations = this.translationService.getTranslations(this.language);
        this.loadExamplePrompts();
        this.demoMessage =  {
            id: "welcome-" + this.language,
            content: this.translations.welcomeMessage,
            role: "assistant",
            timestamp: new Date()
        }
        
        // Tentar obter CDSID do MSAL imediatamente
        this.updateCdsidFromMsal();
        
        // Use setTimeout to avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.initConversationList();
        }, 0);

        // Verificar se é primeiro acesso para mostrar novidades
        this.checkFirstTimeUser();

        this.sessionVerification = setInterval(()=>{
            const newLang = sessionStorage.getItem("language") || "pt-BR"; // Default to pt-BR
            if (newLang !== this.language) {
                this.language = newLang;
                this.translations = this.translationService.getTranslations(this.language);
                this.loadExamplePrompts(); // Recarregar prompts no idioma correto
                this.demoMessage =  {
                    id: "welcome-" + this.language,
                    content: this.translations.welcomeMessage,
                    role: "assistant",
                    timestamp: new Date()
                }
                this.selectConversation(this.activeConversationId);
                this.ref.detectChanges();
            }

            // Tentar atualizar CDSID do MSAL se ainda não tiver
            if (!this.cdsid) {
                this.updateCdsidFromMsal();
            }
            
            const userInfo = sessionStorage.getItem("userInfo");
            if (userInfo && userInfo != this.sessionUserInfoString) {
                try {
                    this.sessionUserInfo = JSON.parse(userInfo);
                    this.state_delta = {
                        ...this.state_delta,
                        "userInfo": this.sessionUserInfo,
                        "bearer_token": this.chatAssistantService.getAuthToken()
                    };
                } catch (e) {
                    console.error('Erro ao parsear userInfo do sessionStorage:', e);
                }
            }
        }, 1000)

        this.eventCallbackId = this.msalService.instance.addEventCallback((message: EventMessage) => {
            if (message.eventType === EventType.HANDLE_REDIRECT_END) {
                console.log(this.msalService.instance.getAllAccounts());
                if (this.msalService.instance.getAllAccounts().length > 0) {
                    this.updateCdsidFromMsal();
                    this.initConversationList();
                }
            }
        });

        if (this.msalService.instance.getAllAccounts().length > 0) {
            this.updateCdsidFromMsal();
            this.initConversationList();
        }
    }
    
    /**
     * Extrai o CDSID do usuário a partir da conta MSAL
     * O CDSID é extraído do username (email) - parte antes do @
     */
    private updateCdsidFromMsal(): void {
        try {
            const accounts = this.msalService.instance.getAllAccounts();
            if (accounts && accounts.length > 0) {
                const account = accounts[0];
                // O username geralmente é o email (ex: gcamarg3@ford.com)
                // Extrair o CDSID (parte antes do @)
                if (account.username) {
                    const cdsid = account.username.split('@')[0].toUpperCase();
                    if (cdsid && cdsid !== this.cdsid) {
                        this.cdsid = cdsid;
                        console.log('CDSID extraído do MSAL:', this.cdsid);
                        
                        // Também salvar no sessionStorage para compatibilidade
                        sessionStorage.setItem('userId', this.cdsid);
                    }
                }
            }
        } catch (error) {
            console.error('Erro ao extrair CDSID do MSAL:', error);
        }
    }


    async initConversationList(){
        // Evitar múltiplas inicializações
        if (this.isInitialized || this.isInitializing) {
            console.log('initConversationList já executado ou em execução, ignorando...');
            return;
        }
        
        this.isInitializing = true;
        
        try {
            // Adicionar um ID temporário ao conjunto de conversas em carregamento para indicar carregamento geral
            const loadingId = 'initial-load';
            this.loadingConversationIds.add(loadingId);
            
            let preConversations = await this.chatAssistantService.getConversationSummaries();
            
            // O backend já filtra conversas deletadas (is_deleted), não é necessário filtrar localmente
            this.conversations = [...this.conversations,
                ...preConversations.map((conv)=> ({id: conv.id, title: conv.preview, isNew: false, isExpired: conv.isExpired || false} as Conversation))
            ];
            
            // Se houver conversas carregadas, selecionar a primeira automaticamente
            if (preConversations.length > 0) {
                // Verificar se há uma conversa mais recente para selecionar
                this.hasEmptyConversations = false;
                const firstConversationId = preConversations[0].id;
                await this.selectConversation(firstConversationId);
            } else {
                // Se não houver conversas anteriores, criar uma nova
                this.createNewConversation(true, false);
            }
            
            // Remover o ID temporário após o carregamento
            this.loadingConversationIds.delete(loadingId);
            
            // Marcar como inicializado
            this.isInitialized = true;
        } catch (error) {
            console.error("Erro ao carregar lista de conversas:", error);
            // Em caso de erro, criar uma nova conversa para garantir que o usuário possa interagir
            this.createNewConversation(true);
            
            // Garantir que o indicador de carregamento seja removido em caso de erro
            this.loadingConversationIds.clear();
            
            // Marcar como inicializado mesmo em caso de erro
            this.isInitialized = true;
        } finally {
            this.isInitializing = false;
        }
    }

    /**
     * Handler para quando uma conversa é deletada.
     * Chama o backend para soft delete e remove a conversa da lista local.
     */
    public async handleConversationDeleted(conversationId: string | number): Promise<void> {
        console.log('Solicitando exclusão da conversa:', conversationId);

        let deletionSucceeded = false;
        try {
            // Chamar o backend para realizar soft delete
            const success = await this.chatAssistantService.deleteConversation(conversationId.toString());

            if (!success) {
                console.error('Falha ao deletar conversa no backend:', conversationId);
            } else {
                deletionSucceeded = true;
            }
        } catch (error) {
            console.error('Erro ao deletar conversa no backend:', error);
        }

        // Se a exclusão não foi bem-sucedida no backend, não remover localmente para evitar inconsistências
        if (!deletionSucceeded) {
            this.ref.detectChanges();
            return;
        }

        // Remover a conversa da lista local
        const conversationIndex = this.conversations.findIndex(conv => conv.id === conversationId);
        if (conversationIndex !== -1) {
            this.conversations = this.conversations.filter(conv => conv.id !== conversationId);
        }
        
        // Se a conversa deletada era a ativa, selecionar outra
        if (this.activeConversationId === conversationId) {
            try {
                if (this.conversations.length > 0) {
                    // Selecionar a primeira conversa disponível
                    await this.selectConversation(this.conversations[0].id);
                } else {
                    // Se não houver mais conversas, criar uma nova
                    await this.createNewConversation(true);
                }
            } catch (error) {
                console.error('Erro ao selecionar/criar conversa após exclusão:', error);
            }
        }
        
        // Forçar detecção de mudanças
        this.ref.detectChanges();
    }


    public async createNewConversation(makeActive: boolean = true, triggerOpen: boolean = true): Promise<void> {
        // Verificar se já existe uma conversa vazia (que só tem a mensagem de boas-vindas)
        const existingEmptyConversation = this.conversations.find(conv => 
            conv.isNew
        );
        
        // Se houver uma conversa vazia, selecionar ela em vez de criar uma nova
        if (existingEmptyConversation) {
            console.log('Conversa vazia encontrada:', existingEmptyConversation.id);
            if (this.activeConversation) {
                this.activeConversation.isActive = false;
            }
            this.activeConversationId = existingEmptyConversation.id;
            this.activeConversation = this.conversations.find((conv)=> conv.id == this.activeConversationId);
            if (this.activeConversation) {
                this.activeConversation.isActive = true;
            }
            this.isMinimized = false;
            return;
        } else {
            this.hasEmptyConversations = true;
        }
        
        // Criar nova conversa no backend primeiro
        let conversationId: string | number = `temp-${Date.now()}`;
        let backendConversationCreated = false;
        try {
            conversationId = await this.chatAssistantService.createNewConversation('web', this.getUserProfile());
            backendConversationCreated = true;
            console.log(`Nova conversa criada no backend com ID: ${conversationId}`);
            
            // Inicializar sessão ADK (somente se a conversa foi criada no backend)
            try {
                await this.chatAssistantService.startSession(this.cdsid, conversationId.toString());
                console.log(`Sessão ADK inicializada para nova conversa ${conversationId}`);
            } catch (sessionError) {
                console.warn(`Erro ao inicializar sessão ADK:`, sessionError);
                // Continuar mesmo se falhar
            }
        } catch (error) {
            console.error('Erro ao criar nova conversa:', error);
            // Manter uma conversa local temporária mesmo em caso de falha no backend
        }

        // Criar nova conversa localmente com o ID do backend (ou temporário, em caso de falha)
        const newConversation: Conversation = {
            id: conversationId,
            messages: [],
            title: `${this.translations.newChatTitle} ${this.conversations.length + 1}`,
            isNew: !backendConversationCreated, // É "new" se ainda não foi persistida no backend
            timestamp: new Date(),
            isActive: true,
            isWaitingResponse: false
        };
        
        // Criar nova referência do array para garantir detecção de mudanças
        this.conversations = [newConversation, ...this.conversations];
        
        // If the chat is closed, open it to show the new conversation
        if (!this.isOpen && triggerOpen) {
            this.openChat();
        }
        if (makeActive || !this.activeConversationId) {
            this.selectConversation(newConversation.id);
        }
    }

    public async selectConversation(conversationId: string | number): Promise<void> {
        // Guard: se conversationId é null ou undefined, não fazer nada
        if (conversationId == null) {
            return;
        }

        this.activeConversation = this.getActiveConversation();
        if (this.activeConversation) {
            this.activeConversation.isActive = false;
        }

        let index = this.conversations.findIndex(conv => conv.id === conversationId);

        // Guard: se a conversa não foi encontrada, não fazer nada
        if (index === -1) {
            console.warn(`Conversa ${conversationId} não encontrada`);
            return;
        }

        this.activeConversationId = conversationId;
        this.activeConversation = this.conversations[index];
        this.activeConversation.isActive = true;
        // this.isMinimized = false
        // Limpar o conjunto de índices de feedback ao trocar de conversa
        this.feedbackMessageIndexes = [];


        if(this.activeConversation.isNew == false && (!this.activeConversation.messages || this.activeConversation.messages.length == 0)) {
            // Adicionar o ID da conversa ao conjunto de conversas em carregamento
            try {
                this.loadingConversationIds.add(conversationId);

                // Inicializar sessão ADK antes de carregar detalhes
                try {
                    await this.chatAssistantService.startSession(this.cdsid, `${conversationId}`);
                    console.log(`Sessão ADK inicializada para conversa ${conversationId}`);
                } catch (sessionError) {
                    console.warn(`Erro ao inicializar sessão ADK para conversa ${conversationId}:`, sessionError);
                    // Continuar mesmo se falhar - o erro será logado mas não impedirá o carregamento
                }

                let response = await this.chatAssistantService.getConversationDetails(`${conversationId}`);
                response.messages.forEach(msg => msg.isComplete = true);
                this.activeConversation.messages = response.messages;

                this.feedbackMessageIndexes = [];
                
                // Processar feedbacks da conversa
                if (response.feedbacks && Array.isArray(response.feedbacks)) {
                    response.feedbacks.forEach(feedback => {
                        if (feedback && typeof feedback.messageIndex === 'number') {
                            // Adicionar ao array de índices
                            this.feedbackMessageIndexes.push(feedback.messageIndex);
                            
                            // Marcar a mensagem como tendo recebido feedback
                            if (this.conversations[index].messages[feedback.messageIndex]) {
                                this.conversations[index].messages[feedback.messageIndex].feedbackDone = true;
                                this.conversations[index].messages[feedback.messageIndex].feedback = feedback;
                            }
                        }
                    });
                }
                
            } finally {
                // Remover o ID da conversa do conjunto após o carregamento (sucesso ou erro)
                this.loadingConversationIds.delete(conversationId);
            }
        }
        
        this.forceViewUpdate();
        this.scrollToBottom();
    }

    public getActiveConversation(): Conversation | undefined {
        if (!this.activeConversationId) return undefined;
        return this.conversations.find(conv => conv.id === this.activeConversationId);
    }

    public getCurrentMessages(): Message[] {
        const activeConv = this.getActiveConversation();
        return activeConv ? activeConv.messages : [];
    }

    private adjustToScreenSize() {
        if (this.isFullscreen || this.isResizing) return; // Não ajustar em fullscreen ou durante resize
        
        const screenWidth = window.innerWidth;
        const screenHeight = window.innerHeight;
        
        // Ajustar tamanho para telas pequenas
        if (screenWidth < 768) {
            this.chatWidth = Math.min(screenWidth - 20, 400);
            this.chatHeight = Math.min(screenHeight - 100, 600);
        } else {
            // Garantir que o chat não exceda os limites da tela apenas se necessário
            if (this.chatWidth > screenWidth - 40) {
                this.chatWidth = Math.min(400, screenWidth - 40);
            }
            if (this.chatHeight > screenHeight - 40) {
                this.chatHeight = Math.min(600, screenHeight - 40);
            }
        }
    }

    public toggleMinimized() {
        console.log('toggleMinimized antes:', this.isMinimized);
        this.isMinimized = !this.isMinimized;
        
        if (this.isMinimized) {
            // Se minimizou, salva a altura atual
            this.savedHeight = this.chatHeight;
            this.isFullscreen = false;
        } else {
            // Se expandiu, restaura a altura
            this.chatHeight = this.savedHeight;
        }
        
        console.log('toggleMinimized depois:', this.isMinimized);
        
        // Força atualização do DOM
        setTimeout(() => {
            const event = new Event('resize');
            window.dispatchEvent(event);
        }, 50);
    }

    public toggleChat() {
        this.isOpen ? this.closeChat(): this.openChat();
    }

    public closeChat() {
        this.isOpen = false;
        this.isMinimized = false;
        this.isFullscreen = false;
    }

    public openChat() {
        this.isOpen = true;
        this.isMinimized = false;
        
        // Se é primeiro acesso, mostrar modal de novidades
        if (this.isFirstTimeUser) {
            this.showNewsModal = true;
            this.showNewsBubble = false;
            this.markNewsSeen();
        }
    }

    public minimizeChat() {
        console.log('minimizeChat');
        // Salvar altura atual antes de minimizar
        if (!this.isMinimized) {
            this.savedHeight = this.chatHeight;
        }
        this.isMinimized = true;
        this.isFullscreen = false;
    }

    public expandChat() {
        console.log('expandChat');
        this.isMinimized = false;
        // Restaurar altura salva
        this.chatHeight = this.savedHeight;
    }

    public toggleFullscreen() {
        this.isFullscreen = !this.isFullscreen;
        if (this.isFullscreen) {
            this.isMinimized = false;
        }
    }

    public resetSize() {
        this.chatWidth = 400;
        this.chatHeight = 600;
        this.isFullscreen = false;
    }

    public onResizeStart(event: MouseEvent) {
        if (this.isFullscreen) return;
        
        this.isResizing = true;
        event.preventDefault();
        
        const startX = event.clientX;
        const startY = event.clientY;
        const startWidth = this.chatWidth;
        const startHeight = this.chatHeight;

        const onMouseMove = (e: MouseEvent) => {
            if (!this.isResizing) return;
            
            const deltaX = startX - e.clientX;
            const deltaY = e.clientY - startY;
            
            // Limites mínimos e máximos
            this.chatWidth = Math.min(Math.max(300, startWidth + deltaX), window.innerWidth - 20);
            this.chatHeight = Math.min(Math.max(400, startHeight - deltaY), window.innerHeight - 20);
        };

        const onMouseUp = () => {
            this.isResizing = false;
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup', onMouseUp);
        };

        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup', onMouseUp);
    }

    public async sendMessage() {
        // Verificar se há mensagem de texto ou anexos
        if (!this.currentMessage.trim() && this.attachedFiles.length === 0) return;
        
        // Limpar qualquer intervalo de animação anterior ANTES de começar nova mensagem
        if (this.typingInterval) {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
        }

        if(this.conversations.length == 0){
            this.createNewConversation(true);
        }

        const activeConv = this.getActiveConversation();
        if (!activeConv) return;

        // Preparar texto da mensagem com informação sobre anexos
        let messageContent = this.currentMessage;
        if (this.attachedFiles.length > 0) {
            const attachmentNames = this.attachedFiles.map(f => f.name).join(', ');
            if (messageContent.trim()) {
                messageContent += `\n\n📎 Anexos: ${attachmentNames}`;
            } else {
                messageContent = `📎 Anexos: ${attachmentNames}`;
            }
        }

        // Add user message
        const userMessage: Message = {
            id: Date.now().toString(),
            content: messageContent,
            role: 'user',
            timestamp: new Date()
        };
        
        // Criar nova referência para messages para garantir detecção de mudanças
        activeConv.messages = [...activeConv.messages, userMessage];
        activeConv.isWaitingResponse = true;

        const messageToSend = this.currentMessage;
        
        // Preparar anexos para envio
        const attachmentsToSend = this.attachedFiles.map(file => ({
            name: file.name,
            type: file.type,
            size: file.size,
            base64: file.base64 || ''
        }));
        
        // Limpar mensagem e anexos
        this.currentMessage = "";
        this.clearAttachments();
        this.isTyping = true;
        
        // Adicionar ID da conversa ativa ao conjunto de conversas em carregamento
        this.loadingConversationIds.add(activeConv.id);

        this.scrollToBottom(); // Scroll after adding user message

        // Criar placeholder para mensagem do assistente
        const assistantMessageId = (Date.now() + 1).toString();
        const assistantMessage: Message = {
            id: assistantMessageId,
            content: '',
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: true,
            isComplete: false,
            toolCalls: [] // Inicializar array de tool calls
        };
        activeConv.messages = [...activeConv.messages, assistantMessage];
        const assistantMessageIndex = activeConv.messages.length - 1;

        try {
            // Usar o ID real da conversa (não mais ID temporário "1")
            const serviceConversationId = activeConv.id;

            // Inicializar sessão ADK antes de enviar a mensagem
            try {
                await this.chatAssistantService.startSession(this.cdsid, serviceConversationId.toString());
                console.log(`Sessão ADK garantida para conversa ${serviceConversationId}`);
            } catch (sessionError) {
                console.warn(`Erro ao garantir sessão ADK:`, sessionError);
                // Continuar mesmo se falhar - a chamada SSE pode funcionar de qualquer forma
            }
            this.state_delta["bearer_token"] = this.chatAssistantService.getAuthToken();
            
            const request: AgentRunRequest = this.chatAssistantService.buildAgentRequest(
                messageToSend,
                this.cdsid,
                serviceConversationId.toString(),
                this.state_delta,
                true,
                attachmentsToSend
            );

            this.state_delta = {};

            let fullResponse = '';
            let displayedContent = '';
            let permanentConversationId: string | undefined = undefined;
            let isAnimating = false;
            let isStreamingComplete = false;
            let currentToolCalls: ToolCall[] = []; // Array para armazenar tool calls

            // Função para animar o texto gradualmente (efeito de digitação)
            const startTypingAnimation = () => {
                // Prevenir múltiplas animações simultâneas
                if (isAnimating || this.typingInterval) return;

                isAnimating = true;
                const charsPerInterval = 3; // Quantos caracteres mostrar por vez
                const intervalMs = 20; // Velocidade da animação (ms)

                this.typingInterval = setInterval(() => {
                    // Se o streaming completou, parar animação imediatamente
                    if (isStreamingComplete) {
                        clearInterval(this.typingInterval);
                        this.typingInterval = null;
                        isAnimating = false;
                        return;
                    }

                    // Se já exibiu tudo que foi recebido, parar temporariamente
                    if (displayedContent.length >= fullResponse.length) {
                        clearInterval(this.typingInterval);
                        this.typingInterval = null;
                        isAnimating = false;
                        return;
                    }

                    // Calcular próximo índice
                    const nextIndex = Math.min(
                        displayedContent.length + charsPerInterval,
                        fullResponse.length
                    );
                    displayedContent = fullResponse.substring(0, nextIndex);

                    // Atualizar mensagem com conteúdo animado
                    activeConv.messages = activeConv.messages.map((msg, idx) => {
                        if (idx === assistantMessageIndex) {
                            return {
                                ...msg,
                                content: displayedContent,
                                isStreaming: true,
                                isComplete: false
                            };
                        }
                        return msg;
                    });

                    this.ref.markForCheck();
                    this.scrollToBottom();
                }, intervalMs);
            };

            // Iniciar streaming SSE
            this.streamSubscription = this.chatAssistantService.runSse(request).subscribe({
                next: (chunk: string) => {
                    try {
                        // Parse do chunk SSE
                        const data = JSON.parse(chunk);
                        
                        // IMPORTANTE: Processar function calls PRIMEIRO, antes de processar erros
                        // Isso garante que a tool call seja criada mesmo quando erro vem junto
                        if (data.content && data.content.parts && Array.isArray(data.content.parts)) {
                            for (const part of data.content.parts) {
                                // Processar nova chamada de função
                                if (part.functionCall) {
                                    console.log('Processando functionCall:', part.functionCall.name);
                                    const toolCallId = part.functionCall.id || `tool-${Array.from(crypto.getRandomValues(new Uint8Array(12)), b => b.toString(16).padStart(2, '0')).join('')}`;
                                    
                                    // IMPORTANTE: Sincronizar com saved ANTES de adicionar nova tool
                                    // para evitar duplicatas
                                    const savedToolCalls = activeConv.messages[assistantMessageIndex].toolCalls || [];
                                    if (savedToolCalls.length > 0 && currentToolCalls.length === 0) {
                                        currentToolCalls = [...savedToolCalls];
                                        console.log('Sincronizando tool calls locais com saved antes de adicionar nova:', currentToolCalls.length);
                                    }
                                    
                                    // Verificar se a tool já existe (por ID ou por nome+pending)
                                    const existingById = currentToolCalls.find(tc => tc.id === toolCallId);
                                    const existingByName = currentToolCalls.find(tc => 
                                        tc.name === part.functionCall.name && tc.status === 'pending'
                                    );
                                    
                                    if (existingById) {
                                        console.log('Tool call já existe por ID, ignorando duplicata:', toolCallId);
                                    } else if (existingByName && part.functionCall.id === undefined) {
                                        // Se já existe uma pending com mesmo nome e não tem ID específico, pode ser duplicata
                                        console.log('Tool call já existe por nome (pending), atualizando args se necessário:', part.functionCall.name);
                                        currentToolCalls = currentToolCalls.map(tc => {
                                            if (tc === existingByName) {
                                                return {
                                                    ...tc,
                                                    args: part.functionCall.args || tc.args
                                                };
                                            }
                                            return tc;
                                        });
                                    } else {
                                        // Adicionar nova tool call
                                        const toolCall: ToolCall = {
                                            id: toolCallId,
                                            name: part.functionCall.name,
                                            args: part.functionCall.args || {},
                                            status: 'pending',
                                            isExpanded: false
                                        };
                                        
                                        currentToolCalls = [...currentToolCalls, toolCall];
                                        console.log('Tool call criada. currentToolCalls:', currentToolCalls.length);
                                    }
                                }
                                
                                // Processar resposta de função
                                if (part.functionResponse) {
                                    console.log('Processando functionResponse:', JSON.stringify(part.functionResponse));
                                    const responseId = part.functionResponse.id;
                                    const responseName = part.functionResponse.name;
                                    const response = part.functionResponse.response;
                                    
                                    console.log('Response ID:', responseId);
                                    console.log('Response Name:', responseName);
                                    console.log('Response content:', JSON.stringify(response));
                                    
                                    // Verificar se há erro na resposta (pode estar em diferentes lugares)
                                    const hasError = response.error || 
                                                     response.status === 'error' || 
                                                     (typeof response === 'string' && response.toLowerCase().includes('error'));
                                    
                                    console.log('Has error:', hasError);
                                    console.log('Tool calls antes de atualizar:', currentToolCalls.map(tc => ({ name: tc.name, status: tc.status })));
                                    
                                    // IMPORTANTE: SEMPRE sincronizar com tool calls salvas na mensagem
                                    // para garantir que temos o estado mais atualizado (ex: status de outras tools)
                                    const savedToolCalls = activeConv.messages[assistantMessageIndex].toolCalls || [];
                                    if (savedToolCalls.length > 0) {
                                        // Mesclar: manter tool calls locais novas + atualizar com status salvos
                                        const savedToolCallsMap = new Map(savedToolCalls.map(tc => [tc.id, tc]));
                                        const localNewToolCalls = currentToolCalls.filter(tc => !savedToolCallsMap.has(tc.id));
                                        
                                        // Usar as salvas como base e adicionar as novas locais
                                        currentToolCalls = [...savedToolCalls, ...localNewToolCalls];
                                        console.log('Sincronizando tool calls com mensagem:', currentToolCalls.length);
                                    }
                                    
                                    // Encontrar a tool call correspondente e atualizar
                                    let foundTool = false;
                                    currentToolCalls = currentToolCalls.map(tc => {
                                        // Match por ID ou por nome (apenas se ainda não foi encontrada e está pending)
                                        const matchById = tc.id === responseId;
                                        const matchByName = tc.name === responseName && tc.status === 'pending';
                                        
                                        if ((matchById || matchByName) && !foundTool) {
                                            console.log('Atualizando tool call:', tc.name, '-> status:', hasError ? 'error' : 'success');
                                            foundTool = true;
                                            return {
                                                ...tc,
                                                result: response,
                                                status: hasError ? 'error' as const : 'success' as const,
                                                error: hasError ? (response.error || response) : undefined,
                                                isExpanded: hasError // Expandir automaticamente em caso de erro
                                            };
                                        }
                                        return tc;
                                    });
                                    
                                    console.log('Tool calls após atualizar:', currentToolCalls.map(tc => ({ name: tc.name, status: tc.status })));
                                }
                            }
                            
                            // Atualizar mensagem com tool calls (se houver alguma)
                            if (currentToolCalls.length > 0) {
                                // Usar ngZone para garantir detecção de mudanças
                                this.ngZone.run(() => {
                                    activeConv.messages = activeConv.messages.map((msg, idx) => {
                                        if (idx === assistantMessageIndex) {
                                            return {
                                                ...msg,
                                                toolCalls: [...currentToolCalls]
                                            };
                                        }
                                        return msg;
                                    });
                                    
                                    this.ref.detectChanges();
                                });
                                this.scrollToBottom();
                            }
                        }
                        
                        // AGORA processar erro (após ter processado function calls)
                        if (data.error) {
                            console.error('Erro recebido via SSE:', data.error);
                            
                            // Usar ngZone.run para garantir que Angular detecte mudanças
                            this.ngZone.run(() => {
                                // Extrair nome da tool do erro se possível
                                const toolNameMatch = data.error.match(/Invalid arguments for tool ([a-zA-Z0-9-_]+):/);
                                const toolName = toolNameMatch ? toolNameMatch[1] : null;
                                
                                console.log('Tool name extraído do erro:', toolName);
                                console.log('Tool calls atuais (local):', currentToolCalls.length, currentToolCalls.map(tc => tc.name));
                                
                                // IMPORTANTE: Também verificar tool calls já salvas na mensagem
                                // (em caso de eventos SSE separados)
                                const currentMessage = activeConv.messages[assistantMessageIndex];
                                const savedToolCalls = currentMessage.toolCalls || [];
                                console.log('Tool calls salvas na mensagem:', savedToolCalls.length, savedToolCalls.map(tc => tc.name));
                                
                                // Usar as tool calls salvas se local estiver vazia
                                if (currentToolCalls.length === 0 && savedToolCalls.length > 0) {
                                    currentToolCalls = [...savedToolCalls];
                                    console.log('Usando tool calls da mensagem salva');
                                }
                                
                                // Procurar tool call existente com status pending para atualizar
                                let foundPendingTool = false;
                                
                                if (currentToolCalls.length > 0) {
                                    // Primeiro: tentar encontrar pelo nome da tool
                                    if (toolName) {
                                        currentToolCalls = currentToolCalls.map(tc => {
                                            if (tc.name === toolName && tc.status === 'pending' && !foundPendingTool) {
                                                console.log('Atualizando tool call pelo nome:', tc.name);
                                                foundPendingTool = true;
                                                return {
                                                    ...tc,
                                                    error: data.error,
                                                    status: 'error' as const,
                                                    isExpanded: true
                                                };
                                            }
                                            return tc;
                                        });
                                    }
                                    
                                    // Segundo: se não encontrou pelo nome, atualizar qualquer pending
                                    if (!foundPendingTool) {
                                        currentToolCalls = currentToolCalls.map(tc => {
                                            if (tc.status === 'pending' && !foundPendingTool) {
                                                console.log('Atualizando primeira tool pending:', tc.name);
                                                foundPendingTool = true;
                                                return {
                                                    ...tc,
                                                    error: data.error,
                                                    status: 'error' as const,
                                                    isExpanded: true
                                                };
                                            }
                                            return tc;
                                        });
                                    }
                                }
                                
                                // Se ainda não encontrou, criar uma nova tool call de erro
                                if (!foundPendingTool) {
                                    console.log('Criando nova tool call de erro (nenhuma pending encontrada)');
                                    const errorToolCall: ToolCall = {
                                        id: `error-${Date.now()}`,
                                        name: toolName || 'unknown-tool',
                                        args: {},
                                        error: data.error,
                                        status: 'error',
                                        isExpanded: true
                                    };
                                    currentToolCalls = [...currentToolCalls, errorToolCall];
                                }
                                
                                console.log('Tool calls após erro:', currentToolCalls.map(tc => ({ name: tc.name, status: tc.status })));
                                
                                // Atualizar mensagem com tool calls e marcar como completa
                                activeConv.messages = activeConv.messages.map((msg, idx) => {
                                    if (idx === assistantMessageIndex) {
                                        return {
                                            ...msg,
                                            toolCalls: [...currentToolCalls],
                                            error: data.error,
                                            isStreaming: false,
                                            isComplete: true
                                        };
                                    }
                                    return msg;
                                });
                                
                                // Limpar estados de carregamento em caso de erro
                                this.loadingConversationIds.delete(activeConv.id);
                                this.isTyping = false;
                                activeConv.isWaitingResponse = false;
                                
                                // Forçar múltiplas detecções de mudanças
                                this.ref.detectChanges();
                                setTimeout(() => {
                                    this.ref.detectChanges();
                                    this.scrollToBottom();
                                }, 0);
                            });
                            return;
                        }
                        
                        // Ignorar mensagens completas finais (partial: false ou undefined)
                        // Processar apenas mensagens parciais (partial: true) ou chamadas de função
                        const isPartialMessage = data.partial === true;
                        const isFunctionCall = data.content.parts.some((part: any) => part.functionCall);
                        const isFunctionResponse = data.content.parts.some((part: any) => part.functionResponse);
                        
                        // Se não é mensagem parcial e não é função, ignorar (é a mensagem completa final)
                        if (!isPartialMessage && !isFunctionCall && !isFunctionResponse && data.content.role === 'model') {
                            console.log('Ignorando mensagem completa final duplicada');
                            return;
                        }

                        // Extrair conteúdo do formato ADK: content.parts[].text
                        let chunkContent = '';
                        if (data.content && data.content.parts && Array.isArray(data.content.parts)) {
                            // Concatenar apenas textos (ignorar functionCall e functionResponse)
                            chunkContent = data.content.parts
                                .filter((part: any) => part.text) // Filtrar apenas parts com text
                                .map((part: any) => part.text || '')
                                .join('');
                        } else if (data.content && typeof data.content === 'string') {
                            // Fallback para conteúdo direto como string
                            chunkContent = data.content;
                        }

                        // Acumular conteúdo apenas se houver texto novo
                        if (chunkContent && isPartialMessage) {
                            fullResponse += chunkContent;

                            // Iniciar ou continuar animação se não estiver rodando
                            if (!isAnimating) {
                                startTypingAnimation();
                            }
                        }


                        // Capturar ID da conversa (apenas para logging, já temos o ID real)
                        if (data.chatid && !permanentConversationId) {
                            permanentConversationId = data.chatid;
                            console.log(`ID da conversa confirmado: ${permanentConversationId}`);
                        }

                    } catch (parseError) {
                        console.error('Erro ao processar chunk SSE:', parseError);
                    }
                },
                error: (error) => {
                    console.error(this.translations.errorMessage, error);

                    // Limpar intervalo de animação
                    if (this.typingInterval) {
                        clearInterval(this.typingInterval);
                        this.typingInterval = null;
                    }

                    // Atualizar mensagem com erro (imutável)
                    activeConv.messages = activeConv.messages.map((msg, idx) => {
                        if (idx === assistantMessageIndex) {
                            return {
                                ...msg,
                                content: `${this.translations.errorMessage}: ${error.message || 'Erro desconhecido'} \n Contact gcamarg3@ford.com`,
                                isStreaming: false,
                                isComplete: true
                            };
                        }
                        return msg;
                    });

                    // Cleanup
                    this.loadingConversationIds.delete(activeConv.id);
                    this.isTyping = false;
                    activeConv.isWaitingResponse = false;
                    this.streamSubscription = null;
                    this.ref.detectChanges();
                },
                complete: () => {
                    console.log('Streaming completo. Resposta final:', fullResponse);

                    // Marcar streaming como completo PRIMEIRO
                    isStreamingComplete = true;

                    // Limpar intervalo de animação IMEDIATAMENTE ao completar
                    if (this.typingInterval) {
                        clearInterval(this.typingInterval);
                        this.typingInterval = null;
                    }
                    isAnimating = false;

                    // IMPORTANTE: Sincronizar com tool calls salvas na mensagem
                    // para garantir que temos o estado mais atualizado
                    const savedToolCalls = activeConv.messages[assistantMessageIndex].toolCalls || [];
                    if (savedToolCalls.length > 0) {
                        // Mesclar: usar saved como base e adicionar novas locais
                        const savedToolCallsMap = new Map(savedToolCalls.map(tc => [tc.id, tc]));
                        const localNewToolCalls = currentToolCalls.filter(tc => !savedToolCallsMap.has(tc.id));
                        currentToolCalls = [...savedToolCalls, ...localNewToolCalls];
                        console.log('Complete: Sincronizando tool calls com mensagem:', currentToolCalls.length);
                    }

                    // IMPORTANTE: Atualizar quaisquer tools que ainda estão "pending" para "success"
                    // pois se o streaming completou sem erro, significa que as tools foram executadas
                    const hasPendingTools = currentToolCalls.some(tc => tc.status === 'pending');
                    if (hasPendingTools) {
                        console.log('Complete: Atualizando tools pending para success');
                        currentToolCalls = currentToolCalls.map(tc => {
                            if (tc.status === 'pending') {
                                console.log('  - Atualizando tool pending para success:', tc.name);
                                return {
                                    ...tc,
                                    status: 'success' as const,
                                    // Manter result se já existe, ou marcar como executado
                                    result: tc.result || { status: 'completed' }
                                };
                            }
                            return tc;
                        });
                    }

                    // Garantir que todo o conteúdo seja exibido (usar fullResponse diretamente)
                    // Fazer apenas UMA atualização final
                    activeConv.messages = activeConv.messages.map((msg, idx) => {
                        if (idx === assistantMessageIndex) {
                            return {
                                ...msg,
                                content: fullResponse, // Usar fullResponse diretamente
                                toolCalls: currentToolCalls.length > 0 ? [...currentToolCalls] : msg.toolCalls,
                                isStreaming: false,
                                isComplete: true
                            };
                        }
                        return msg;
                    });

                    // Cleanup
                    this.loadingConversationIds.delete(activeConv.id);
                    this.isTyping = false;
                    activeConv.isWaitingResponse = false;
                    this.streamSubscription = null;

                    if(this.activeConversationId == activeConv.id){
                        this.scrollToBottom();
                    }

                    this.ref.detectChanges();
                }
            });

        } catch (error) {
            console.error('Erro ao iniciar streaming:', error);

            // Limpar intervalo de animação em caso de erro
            if (this.typingInterval) {
                clearInterval(this.typingInterval);
                this.typingInterval = null;
            }

            // Atualizar mensagem com erro
            activeConv.messages = activeConv.messages.map((msg, idx) => {
                if (idx === assistantMessageIndex) {
                    return {
                        ...msg,
                        content: `${this.translations.errorMessage}: ${error.message} \n Contact gcamarg3@ford.com`,
                        isStreaming: false,
                        isComplete: true
                    };
                }
                return msg;
            });

            // Cleanup
            this.loadingConversationIds.delete(activeConv.id);
            this.ref.detectChanges();
            this.isTyping = false;
            activeConv.isWaitingResponse = false;
        }
    }

    public onKeyPress(event: KeyboardEvent) {
        // Navegação nas sugestões
        if (this.showSuggestions && this.filteredSuggestions.length > 0) {
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedSuggestionIndex = Math.min(
                    this.selectedSuggestionIndex + 1, 
                    this.filteredSuggestions.length - 1
                );
                this.scrollSuggestionIntoView();
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedSuggestionIndex = Math.max(this.selectedSuggestionIndex - 1, 0);
                this.scrollSuggestionIntoView();
                return;
            }
            if (event.key === 'Enter' && this.selectedSuggestionIndex >= 0) {
                event.preventDefault();
                this.selectSuggestion(this.filteredSuggestions[this.selectedSuggestionIndex]);
                return;
            }
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closeSuggestions();
                return;
            }
        }

        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            this.sendMessage();
        }
    }

    // === Sistema de Sugestões Autocomplete ===
    
    public get filteredSuggestions(): Array<{text: string, icon: string}> {
        // Mostrar todas as sugestões quando o usuário começar a digitar
        if (!this.currentMessage || this.currentMessage.trim().length < 1) {
            return [];
        }
        return this.examplePrompts;
    }

    public onInputChange(): void {
        const hasText = this.currentMessage && this.currentMessage.trim().length >= 1;
        
        if (hasText && this.examplePrompts.length > 0) {
            this.showSuggestions = true;
            this.selectedSuggestionIndex = -1;
        } else {
            this.closeSuggestions();
        }
    }

    public onInputFocus(): void {
        // Mostrar sugestões se houver texto suficiente e matches
        if (this.currentMessage && this.currentMessage.trim().length >= 1 && this.filteredSuggestions.length > 0) {
            this.showSuggestions = true;
        }
    }

    public onInputBlur(): void {
        // Usar timeout para permitir clique nas sugestões antes de fechar
        setTimeout(() => {
            this.closeSuggestions();
        }, 200);
    }

    public selectSuggestion(suggestion: {text: string, icon: string}): void {
        this.currentMessage = suggestion.text;
        this.closeSuggestions();
        
        // Focar no textarea
        setTimeout(() => {
            const textarea = document.querySelector('.message-input') as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        }, 50);
    }

    public closeSuggestions(): void {
        this.showSuggestions = false;
        this.selectedSuggestionIndex = -1;
    }

    private scrollSuggestionIntoView(): void {
        setTimeout(() => {
            const selectedItem = document.querySelector('.suggestion-item.selected') as HTMLElement;
            if (selectedItem) {
                selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
        }, 0);
    }

    private scrollToBottom() {
        setTimeout(() => {
            const messagesContainer = document.querySelector('.chat-messages'); // This needs to be specific to the active chat if UI changes
            if (messagesContainer) {
                messagesContainer.scrollTop = messagesContainer.scrollHeight;
            }
        }, 100);
    }    // Verifica se uma conversa está vazia (só contém a mensagem de boas-vindas)
    public isConversationEmpty(conversationId: string | number): boolean {
        const conversation = this.conversations.find(conv => conv.id === conversationId);
        if (!conversation) return false;
        
        // Verificar se a conversa ainda está sendo carregada
        if (this.isConversationLoading(conversationId)) return false;
        
        // Verificar se a conversa tem mensagens
        if (!conversation.messages || conversation.messages.length === 0) return true;
        
        return conversation.messages.length === 1 && 
               conversation.messages[0].role !== 'user' && 
               conversation.messages[0].content === this.translations.welcomeMessage;
    }
    

    // Verifica se uma conversa específica está sendo carregada
    public isConversationLoading(conversationId: string | number): boolean {
        return this.loadingConversationIds.has(conversationId);
    }
    
    // Verifica se existe alguma operação de carregamento em andamento
    public isAnyConversationLoading(): boolean {
        return this.loadingConversationIds.size > 0;
    }

    // Limpa todos os indicadores de carregamento (útil em casos de erro ou na inicialização)
    private clearAllLoadingIndicators(): void {
        this.loadingConversationIds.clear();
        this.isTyping = false;
    }
    
    // Método utilitário para forçar atualização da view
    private forceViewUpdate(): void {
        // Detectar mudanças e depois garantir que o ciclo de detecção seja executado
        this.ref.detectChanges();
        setTimeout(() => this.ref.detectChanges(), 0);
    }
    
    // Método para cancelar streaming em andamento
    public cancelStreaming(): void {
        if (this.streamSubscription) {
            this.streamSubscription.unsubscribe();
            this.streamSubscription = null;
            this.isTyping = false;
        }

        // Limpar animação de digitação
        if (this.typingInterval) {
            clearInterval(this.typingInterval);
            this.typingInterval = null;
        }

        const activeConv = this.getActiveConversation();
        if (activeConv) {
            activeConv.isWaitingResponse = false;
            this.loadingConversationIds.delete(activeConv.id);
        }

        console.log('Streaming cancelado pelo usuário');
        this.ref.detectChanges();
    }

    // === Métodos para sistema de novidades ===
    
    private checkFirstTimeUser(): void {
        const newsSeen = localStorage.getItem(this.NEWS_SEEN_KEY);
        this.isFirstTimeUser = !newsSeen;
        this.showNewsBubble = this.isFirstTimeUser;
    }

    private markNewsSeen(): void {
        localStorage.setItem(this.NEWS_SEEN_KEY, new Date().toISOString());
        this.isFirstTimeUser = false;
        this.showNewsBubble = false;
    }

    public closeNewsModal(): void {
        this.showNewsModal = false;
    }

    public dismissNewsBubble(): void {
        this.showNewsBubble = false;
        // Não marca como visto - só fecha o bubble
    }

    // === Métodos para prompts de exemplo ===
    
    private loadExamplePrompts(): void {
        const prompts = this.translations.examplePrompts;
        if (prompts) {
            this.examplePrompts = [
                { text: prompts.backorder, icon: 'fa-question-circle' },
                { text: prompts.invoiceStatus, icon: 'fa-file-text' },
                { text: prompts.workOrders, icon: 'fa-list-alt' },
                { text: prompts.creditLimit, icon: 'fa-credit-card' },
                { text: prompts.addDescription, icon: 'fa-plus-circle' },
                { text: prompts.breakSupersession, icon: 'fa-unlink' }
            ];
        }
    }
    
    public selectExamplePrompt(prompt: string): void {
        this.currentMessage = prompt;
        // Focar no textarea após inserir o prompt
        setTimeout(() => {
            const textarea = document.querySelector('.message-input') as HTMLTextAreaElement;
            if (textarea) {
                textarea.focus();
                // Posicionar cursor no final
                textarea.setSelectionRange(textarea.value.length, textarea.value.length);
            }
        }, 100);
    }

    // Implementação do OnDestroy para limpar os indicadores quando o componente é destruído
    ngOnDestroy(): void {
        this.cancelStreaming();
        this.clearAllLoadingIndicators();
        this.clearAttachments(); // Limpar anexos ao destruir componente
        if (this.sessionVerification) {
            clearInterval(this.sessionVerification);
        }
    }

    public triggerFileInput(): void {
        const fileInput = document.getElementById('attachment-input') as HTMLInputElement;
        if (fileInput) {
            fileInput.click();
        }
    }

    /**
     * Handler para seleção de arquivos
     */
    public onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (!input.files || input.files.length === 0) return;

        const files = Array.from(input.files);
        this.addFiles(files);
        
        // Limpar input para permitir selecionar o mesmo arquivo novamente
        input.value = '';
    }

    /**
     * Adiciona arquivos à lista de anexos
     */
    private async addFiles(files: File[]): Promise<void> {
        for (const file of files) {
            // Verificar limite de arquivos
            if (this.attachedFiles.length >= this.maxFiles) {
                console.warn(`Limite máximo de ${this.maxFiles} arquivos atingido`);
                break;
            }

            // Verificar tipo de arquivo
            if (!this.isFileTypeAllowed(file)) {
                console.warn(`Tipo de arquivo não permitido: ${file.type}`);
                continue;
            }

            // Verificar tamanho do arquivo
            if (file.size > this.maxFileSize) {
                console.warn(`Arquivo muito grande: ${file.name} (${this.formatFileSize(file.size)})`);
                continue;
            }

            // Verificar se o arquivo já foi adicionado
            if (this.attachedFiles.some(af => af.name === file.name && af.size === file.size)) {
                console.warn(`Arquivo já anexado: ${file.name}`);
                continue;
            }

            const attachedFile: AttachedFile = {
                file: file,
                name: file.name,
                size: file.size,
                type: file.type
            };

            // Gerar preview para imagens
            if (file.type.startsWith('image/')) {
                attachedFile.preview = await this.generateImagePreview(file);
            }

            // Converter para base64
            attachedFile.base64 = await this.fileToBase64(file);

            this.attachedFiles = [...this.attachedFiles, attachedFile];
        }

        this.ref.detectChanges();
    }

    /**
     * Remove um arquivo da lista de anexos
     */
    public removeAttachment(index: number): void {
        if (index >= 0 && index < this.attachedFiles.length) {
            // Revogar URL de preview se existir
            const file = this.attachedFiles[index];
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }

            this.attachedFiles = this.attachedFiles.filter((_, i) => i !== index);
            this.ref.detectChanges();
        }
    }

    /**
     * Limpa todos os anexos
     */
    public clearAttachments(): void {
        // Revogar todas as URLs de preview
        this.attachedFiles.forEach(file => {
            if (file.preview) {
                URL.revokeObjectURL(file.preview);
            }
        });
        this.attachedFiles = [];
        this.ref.detectChanges();
    }

    /**
     * Verifica se o tipo de arquivo é permitido
     */
    private isFileTypeAllowed(file: File): boolean {
        if (this.allowedFileTypes.includes(file.type)) {
            return true;
        }
        // Fallback por extensão (ex: .msg pode não ter MIME type reconhecido pelo browser)
        const parts = file.name.split('.');
        const ext = parts.length > 1 ? '.' + parts[parts.length - 1].toLowerCase() : '';
        return this.allowedExtensions.includes(ext);
    }

    /**
     * Gera preview para imagens
     */
    private generateImagePreview(file: File): Promise<string> {
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
                resolve(e.target.result as string);
            };
            reader.readAsDataURL(file);
        });
    }

    /**
     * Converte arquivo para base64
     */
    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                const base64 = reader.result as string;
                // Remover o prefixo data:type;base64,
                const base64Data = base64.split(',')[1];
                resolve(base64Data);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }

    /**
     * Formata o tamanho do arquivo para exibição
     */
    public formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    /**
     * Retorna o ícone apropriado para o tipo de arquivo
     */
    public getFileIcon(type: string): string {
        if (type.startsWith('image/')) return 'fa-file-image-o';
        if (type === 'application/pdf') return 'fa-file-pdf-o';
        if (type.includes('spreadsheet') || type.includes('excel')) return 'fa-file-excel-o';
        if (type.includes('word') || type.includes('document')) return 'fa-file-word-o';
        if (type.startsWith('text/')) return 'fa-file-text-o';
        if (type === 'application/vnd.ms-outlook') return 'fa-envelope-o';
        if (type.startsWith('video/')) return 'fa-file-video-o';
        return 'fa-file-o';
    }

    /**
     * Verifica se pode adicionar mais arquivos
     */
    public canAddMoreFiles(): boolean {
        return this.attachedFiles.length < this.maxFiles;
    }

    /**
     * Retorna os tipos de arquivo aceitos para o input
     */
    public getAcceptedFileTypes(): string {
        return this.allowedFileTypes.join(',') + ',' + this.allowedExtensions.join(',');
    }

    public onFeedbackRequested(event: {message: Message, index: number}): void {
        this.openFeedbackModal(event.message, event.index);
    }

    // Métodos para gerenciar o feedback
    public openFeedbackModal(message: Message, index: number): void {
        this.currentFeedbackMessage = message;
        this.feedbackRating = message.feedback ? message.feedback.rating : 0;
        this.feedbackComment = message.feedback ? message.feedback.comment || '' : '';
        this.currentFeedbackMessage.index = index;
        this.showFeedbackModal = true;
    }

    public closeFeedbackModal(event?: MouseEvent): void {
        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }
        
        this.showFeedbackModal = false;
        this.currentFeedbackMessage = null;
        this.feedbackRating = 0;
        this.feedbackComment = '';
    }

    public setFeedbackRating(rating: number): void {
        this.feedbackRating = rating;
    }

    public async submitFeedback(feedback: ChatFeedback): Promise<void> {
        if (!this.currentFeedbackMessage || !this.activeConversationId || !this.currentFeedbackMessage.index) {
            return;
        }

        try {
            this.isSendingFeedback = true;
            
            feedback.messageIndex = this.currentFeedbackMessage.index;
            
            // Enviar feedback para o serviço
            const success = await this.chatAssistantService.sendFeedback(
                feedback,
                this.activeConversationId.toString(),
            );
            
            if (success) {
                // Garantir que o índice é um número válido
                const messageIndexOfFeedback = typeof this.currentFeedbackMessage.index === 'number' ? 
                    this.currentFeedbackMessage.index : -1;
                
                if (messageIndexOfFeedback >= 0) {
                    // Adicionar o índice ao conjunto de mensagens com feedback se ainda não estiver lá
                    if (!this.feedbackMessageIndexes.includes(messageIndexOfFeedback)) {
                        this.feedbackMessageIndexes.push(messageIndexOfFeedback);
                        this.feedbackMessageIndexes = [...this.feedbackMessageIndexes];
                    }
                    
                    // Atualizar a mensagem com o feedback enviado
                    if (this.activeConversation && this.activeConversation.messages) {
                        // Atualização mais segura com verificações de tipo
                        if (messageIndexOfFeedback >= 0 && 
                            messageIndexOfFeedback < this.activeConversation.messages.length) {
                            
                            // Atualizar a mensagem com o feedback
                            this.activeConversation.messages[messageIndexOfFeedback].feedback = feedback;
                            this.activeConversation.messages[messageIndexOfFeedback].feedbackDone = true;
                            
                            // Criar nova referência para forçar detecção de mudanças
                            this.activeConversation.messages = [...this.activeConversation.messages];
                            
                        }
                    }
                }
                
                this.closeFeedbackModal();
            } else {
                // Exibir mensagem de erro (se desejar)
                // this.showNotification('Erro ao enviar feedback. Tente novamente.', 'error');
            }
        } catch (error) {
            console.error('Erro ao enviar feedback:', error);
            // this.showNotification('Erro ao enviar feedback. Tente novamente.', 'error');
        } finally {
            this.isSendingFeedback = false;
            this.forceViewUpdate();
        }
    }
}

/* Um commit post-mortem (de funcionario)

    Bem, depois de alguns anos aqui, deixo meu ultimo commit.
    Poeticamente, aqui é o onde o Wings começa e por favor terminem a modernização kkkkkk
    Aproveitem o espaço que temos aqui dentro, sugiram, melhorem, pensem fora da caixa.
    Saio com um pequeno pesar mas com uma vontade de voar maior ainda.
    Obrigado a todos que fizeram parte dessa jornada, foi incrível crescer e aprender com vocês.
    (e sim, o codigo desse chat ta horrivel, quando mudarem o angular da para melhorar)
    - Gabriel Camargo

*/